import { apiClient } from './client'
import type { AchievementUnlock } from './achievements'
import type { RecommendationExplanation } from './reading'

export type LearningStepType = 'REVIEW' | 'READ' | 'CHALLENGE' | 'COACH' | 'PRACTICE' | 'ONBOARDING_ADD_WORDS'

export interface LearningStep {
  type: LearningStepType
  title: string
  estimatedMinutes: number
  config: Record<string, unknown>
  reasons?: string[]
  narration?: string | null
  explanations?: RecommendationExplanation[]
}

export interface LearningPlan {
  estimatedMinutes: number
  stepCount: number
  stepsPreview: Array<{
    type: LearningStepType
    title: string
    estimatedMinutes: number
    detail?: string | null
  }>
  canStart: boolean
  resumeSessionId: string | null
  guidedSessionCompletedToday: boolean
  ctaLabel: 'CONTINUE' | 'KEEP'
  lastCompletedSessionId: string | null
  coachNote: CoachNote | null
}

export interface CoachNote {
  id: string
  type: string
  message: string
  ctaLabel: string
  ctaAction: 'CHALLENGE' | 'PRACTICE' | 'WORD_DETAIL'
  wordIds: string[]
  focusUserWordId: string | null
  /** Structured params for UI locale templates (Pattern B). */
  messageParams?: Record<string, unknown> | null
}

export interface LearningSuggestion {
  code: string
  title: string
  description: string
  estimatedMinutes: number
  path: string
}

export interface LearningSuggestions {
  guidedSessionCompletedToday: boolean
  suggestions: LearningSuggestion[]
}

export interface LearningSessionSummary {
  durationSeconds: number
  wordsRefreshed: number
  correctCount: number
  articlesRead: number
  challengeScore: number
  currentStreak: number
  streakUpdated: boolean
  tomorrowDueCount: number
  growthHighlight: { message: string } | null
  momentumClosing: {
    closingMessage: string
    tomorrowPreview: {
      estimatedMinutes: number
      dueWordCount: number
      continueArticleId: string | null
      continueArticleTitle: string | null
      message: string
    }
  } | null
  todayProgress: TodayProgress
  newAchievements: AchievementUnlock[]
}

export interface TodayProgress {
  wordsRefreshed: number
  articlesRead: number
  wordsLearned: number
  testsCompleted: number
  practiceSetsCompleted: number
}

export interface MomentumPayload {
  opening: { message: string; linkedWordTexts: string[] } | null
  closing: LearningSessionSummary['momentumClosing']
}

export interface LearningSession {
  id: string
  status: string
  currentStepIndex: number
  plannedDurationMinutes: number
  templateCode: string
  steps: LearningStep[]
  currentStep: LearningStep | null
  completed: boolean
  summary: LearningSessionSummary | null
  momentum: MomentumPayload | null
}

export interface CompleteLearningStepRequest {
  cardsReviewed?: number
  correctCount?: number
  durationMs?: number
}

export const learningApi = {
  getPlan: () => apiClient.get<LearningPlan>('/learning/plan').then((r) => r.data),

  getSuggestions: () =>
    apiClient.get<LearningSuggestions>('/learning/suggestions').then((r) => r.data),

  startSession: () => apiClient.post<LearningSession>('/learning/session/start').then((r) => r.data),

  getSession: (id: string) =>
    apiClient.get<LearningSession>(`/learning/session/${id}`).then((r) => r.data),

  completeStep: (id: string, stepIndex: number, body?: CompleteLearningStepRequest) =>
    apiClient
      .post<LearningSession>(`/learning/session/${id}/steps/${stepIndex}/complete`, body ?? {})
      .then((r) => r.data),

  completeSession: (id: string) =>
    apiClient.post<LearningSession>(`/learning/session/${id}/complete`).then((r) => r.data),

  abandonSession: (id: string) => apiClient.post<void>(`/learning/session/${id}/abandon`),

  startMiniSession: () =>
    apiClient.post<LearningSession>('/learning/mini-session/start').then((r) => r.data),

  getExploreSessionStatus: () =>
    apiClient.get<ExploreSessionStatus>('/learning/explore-session').then((r) => r.data),

  dismissExploreSession: () => apiClient.post<void>('/learning/explore-session/dismiss'),

  dismissInsight: (id: string) => apiClient.post<void>(`/learning/insights/${id}/dismiss`),
}

export interface ExploreSessionStatus {
  wordsAdded: number
  threshold: number
  shouldPromptMiniSession: boolean
}
